/**
 * SolfegeButtons — seven syllable buttons for scale-degree exercises.
 *
 * Generic over the solfège system — syllables are passed as props.
 * Selection state and feedback styling are driven by props.
 */

import { type CSSProperties, type ReactNode } from "react";

export interface SolfegeButton {
  syllable: string;
  /** Optional letter name shown beneath the syllable. */
  letterName?: string;
  /** Mark this button as the user's selection. */
  selected?: boolean;
  /** Mark this as the correct answer. */
  correct?: boolean;
  /** Mark this as the user's wrong selection. */
  wrong?: boolean;
}

export interface SolfegeButtonsProps {
  buttons: SolfegeButton[];
  disabled?: boolean;
  onSelect: (index: number) => void;
}

export function SolfegeButtons({ buttons, disabled = false, onSelect }: SolfegeButtonsProps): ReactNode {
  return (
    <div className="grid grid-cols-7 gap-2">
      {buttons.map((btn, i) => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(i)}
          aria-pressed={btn.selected && !btn.correct && !btn.wrong ? true : undefined}
          aria-label={buildSyllableLabel(btn)}
          className={getButtonClass(btn)}
          style={getButtonStyle(btn)}
        >
          <span className="block font-serif italic text-xl font-medium">{btn.syllable}</span>
          {btn.letterName && (
            <span className="block font-mono text-[10px] mt-1 text-current opacity-70">
              {btn.letterName}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Surface correctness in the aria-label during graded state — visual
 * cues (color + line-through) alone don't reach screen-reader users.
 */
function buildSyllableLabel(btn: SolfegeButton): string {
  const syllable = btn.syllable;
  if (btn.correct) return `${syllable} (correct answer)`;
  if (btn.wrong) return `${syllable} (your selection — incorrect)`;
  return `Select solfège syllable ${syllable}`;
}

function getButtonClass(btn: SolfegeButton): string {
  const shared = "py-4 px-1 border transition-colors";
  if (btn.correct) return `${shared} bg-success/10 border-success text-success`;
  if (btn.wrong)   return `${shared} bg-danger/10 border-danger text-danger`;
  const interactive = `${shared} border-ink/20 disabled:opacity-40 disabled:cursor-not-allowed`;
  if (btn.selected) return `${interactive} bg-paperWarm`;
  return `${interactive} bg-paper hover:bg-ink hover:text-paper hover:border-ink`;
}

function getButtonStyle(_btn: SolfegeButton): CSSProperties {
  // Reserved for runtime styles that Tailwind can't express.
  return {};
}
